import { Component, ErrorInfo, FC, ReactNode, useEffect } from 'react'

/*
Note: ErrorBoundary do not catch errors for:
- Event handlers
- Asynchronous code (e.g. setTimeout or requestAnimationFrame callbacks)
- Server side rendering
- Errors thrown in the error boundary itself (rather than its children)
*/

interface ErrorBoundaryProps {
  getErrorMessage?: (params: {
    error: Error
    info: ErrorInfo
  }) => Error | string
  renderError?: (error: Error) => ReactNode
  children: ReactNode
  state: ErrorBoundaryState
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

const ErrorBoundary: FC<ErrorBoundaryProps> = ({
  getErrorMessage = (x) => x.error,
  renderError = () => null,
  children,
  state,
}) => {
  useEffect(() => {
    if (state.hasError && state.error) {
      const err = getErrorMessage({ error: state.error, info: {} as ErrorInfo })

      // Log to New Relic Browser
      if (window.NREUM) {
        window.NREUM.noticeError(err)
      }

      // Always log errors to the console
      console.error(state.error)
    }
  }, [state.hasError, state.error, getErrorMessage])

  if (state.hasError && state.error) {
    return renderError(state.error)
  }

  return children
}

// Error boundary needs to be a class component to use error boundary features
class ErrorBoundaryWrapper extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ error })
  }

  render(): ReactNode {
    return <ErrorBoundary {...this.props} state={this.state} />
  }
}

export default ErrorBoundaryWrapper
